import { createClient } from "@supabase/supabase-js";
import { validateInventory } from "./inventory";

const url = process.env.REACT_APP_SUPABASE_URL;
const key = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
export const liveInventoryConfigured = Boolean(url && key);
let client;
export function getInventoryClient() {
  if (!liveInventoryConfigured) return null;
  if (key.startsWith("sb_secret_")) throw new Error("Use a publishable key, never a secret key, in website configuration.");
  if (key.startsWith("eyJ")) {
    try { if (JSON.parse(atob(key.split(".")[1])).role === "service_role") throw new Error("Service-role keys must never be used in the website."); }
    catch (error) { if (error.message.includes("Service-role")) throw error; }
  }
  if (!client) client = createClient(url, key);
  return client;
}

export async function readLiveInventory(signal) {
  const client = getInventoryClient();
  let query = client.from("storefront_inventory").select("items, revision").eq("id", 1).single();
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query;
  if (error) throw new Error(`Cannot load live inventory: ${error.message}`);
  return { items: validateInventory(data.items), revision: data.revision };
}

export async function publishLiveInventory(items, revision) {
  if (!Number.isSafeInteger(revision)) throw new Error("Reload the editor to load the latest inventory revision.");
  const { data, error } = await getInventoryClient().rpc("publish_storefront_inventory", { new_items: validateInventory(items), expected_revision: revision });
  if (error) throw new Error(error.message);
  return data;
}

export async function uploadProductPhotos(files) {
  if (!files.length) throw new Error("Choose at least one photo.");
  for (const file of files) {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Photos must be JPG, PNG or WebP.");
    if (file.size > 10 * 1024 * 1024) throw new Error("Each photo must be 10 MB or smaller.");
  }
  const storage = getInventoryClient().storage.from("product-photos");
  const paths = [];
  try {
    for (const file of files) {
      const extension = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" }[file.type];
      const objectPath = `${crypto.randomUUID()}.${extension}`;
      const { error } = await storage.upload(objectPath, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(`Photo upload failed: ${error.message}`);
      paths.push(objectPath);
    }
    return paths.map(p => `storage:product-photos/${p}`);
  } catch (error) {
    if (paths.length) await storage.remove(paths);
    throw error;
  }
}
